import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Users, Save, Loader2, TestTube, Clock, Building2, Download } from "lucide-react";

const ManagerReportsCard = React.memo(function ManagerReportsCard({
  teamLeaderReportEnabled,
  setTeamLeaderReportEnabled,
  teamLeaderReportTime,
  setTeamLeaderReportTime,
  includeTasksToday,
  setIncludeTasksToday,
  includeChallenges,
  setIncludeChallenges,
  includePlanTomorrow,
  setIncludePlanTomorrow,
  includeAiDiscovery,
  setIncludeAiDiscovery,
  savingTeamLeaderReport,
  handleSaveTeamLeaderReport,
  testReportDialogOpen,
  setTestReportDialogOpen,
  teamLeaders,
  selectedTeamLeaders,
  loadingTeamLeaders,
  generatingReport,
  sendReportToLeaders,
  setSendReportToLeaders,
  openTestReportDialog,
  toggleTeamLeaderSelection,
  selectAllTeamLeaders,
  deselectAllTeamLeaders,
  handleGenerateTestReport,
}) {
  return (
    <>
      {/* Manager Reports Scheduling */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            <CardTitle>Automated Manager Reports</CardTitle>
          </div>
          <CardDescription>
            Schedule automated manager reports to be sent daily. Each manager receives their team&apos;s report with form-based fields. All reports CC&apos;d to taj@jobtalk.ai.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Checkbox
              id="enable-manager-reports"
              checked={teamLeaderReportEnabled}
              onCheckedChange={setTeamLeaderReportEnabled}
            />
            <Label htmlFor="enable-manager-reports" className="text-sm font-medium cursor-pointer">
              Enable Automated Manager Reports
            </Label>
          </div>

          {teamLeaderReportEnabled && (
            <div className="space-y-4 pl-4 border-l-2 border-purple-200">
              <div className="space-y-2">
                <Label htmlFor="manager-report-time">Time to Send Report</Label>
                <Select value={teamLeaderReportTime} onValueChange={setTeamLeaderReportTime}>
                  <SelectTrigger id="manager-report-time" className="w-48">
                    <Clock className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="08:00">08:00 AM ET</SelectItem>
                    <SelectItem value="09:00">09:00 AM ET</SelectItem>
                    <SelectItem value="10:00">10:00 AM ET</SelectItem>
                    <SelectItem value="11:00">11:00 AM ET</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Reports will be sent at this time on weekdays (Monday-Friday). Each manager gets their team&apos;s data only.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  📋 <strong>What&apos;s Included:</strong> Each manager report uses form fields assigned to their team members. Reports are sent individually to each manager with CC to taj@jobtalk.ai.
                </p>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveTeamLeaderReport} disabled={savingTeamLeaderReport}>
              {savingTeamLeaderReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Manager Report Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Report Dialog */}
      <Dialog open={testReportDialogOpen} onOpenChange={setTestReportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-primary" />
              Generate Test Report
            </DialogTitle>
            <DialogDescription>
              Select which Team Leaders' reports to generate. The report will be downloaded as a PDF (no email will be sent).
              Report is generated for the previous working day.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingTeamLeaders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : teamLeaders.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No Team Leaders found</p>
                <p className="text-sm">Make sure teams have leaders assigned</p>
              </div>
            ) : (
              <>
                {/* Select All / Deselect All */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedTeamLeaders.length} of {teamLeaders.length} selected
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllTeamLeaders}>
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAllTeamLeaders}>
                      Deselect All
                    </Button>
                  </div>
                </div>

                {/* Team Leaders List */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {teamLeaders.map((leader) => (
                    <div
                      key={leader.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedTeamLeaders.includes(leader.id)
                          ? 'bg-primary/5 border-primary/30'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                      onClick={() => toggleTeamLeaderSelection(leader.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedTeamLeaders.includes(leader.id)}
                          onCheckedChange={() => toggleTeamLeaderSelection(leader.id)}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{leader.full_name}</p>
                          <p className="text-xs text-slate-500">{leader.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {leader.teams.map((team) => (
                          <div key={team.id} className="flex items-center gap-1 text-xs text-slate-600">
                            <Building2 className="h-3 w-3" />
                            {team.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Send to Team Leaders Checkbox */}
                <div className="mt-4 pt-4 border-t">
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer"
                    onClick={() => setSendReportToLeaders(!sendReportToLeaders)}
                  >
                    <Checkbox
                      checked={sendReportToLeaders}
                      onCheckedChange={setSendReportToLeaders}
                    />
                    <div>
                      <p className="font-medium text-blue-900">Send PDF copy to Team Leaders</p>
                      <p className="text-xs text-blue-600">Email the generated report to selected team leaders</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTestReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateTestReport}
              disabled={generatingReport || selectedTeamLeaders.length === 0}
            >
              {generatingReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate & Download PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default ManagerReportsCard;
